import { useRef, useEffect, useCallback } from "react";

/**
 * A custom hook for executing a callback periodically.
 *
 * To change the interval without causing an additional delay,
 * remember to set the delay to zero while changing the interval.
 *
 * @param {Function} callback
 *   A callback to be executed every interval.
 *
 * @param {number} interval
 *   The milliseconds between the completion of one fetch and the start of the next.
 *
 * @param {number} [delay]
 *   Optional number of milliseconds to wait before doing the first polling.
 */
export function useInterval({callback, interval, delay = 0}) {

  const callback_ = useRef(callback);
  const intervalId = useRef(null);
  const timeoutId = useRef(null);

  useEffect(() => {
    callback_.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    clearInterval(intervalId.current);
    intervalId.current = setInterval(() => callback_.current(), interval);
  }, [interval]);

  useEffect(() => {
    if (!delay) {
      start();
      return () => clearInterval(intervalId.current);
    }

    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(start, delay);

    return () => {
      clearTimeout(timeoutId.current);
      clearInterval(intervalId.current);
    }
  }, [delay, start]);
}

